import { useLazyQuery, useQuery } from '@apollo/client';
import cx from 'classnames';
import pick from 'lodash/pick';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { mp, getDistinctId, getDeviceId } from '../util/analytics';
import { aprAddressCheck, aprBookingStart, postal3 } from '../util/aperioTracking';
import {
  addressFieldsRefused,
  checkedAddressFields,
} from '../util/coverageAddress';
import { getBookingLang } from '../util/bookingLang';
import { isPortalI18nActive } from '../i18n';
import Button from '../components/Button';
import Form from '../components/Form';
import { Column, Container, Row } from '../components/Grid';
import Loader from '../components/Loader';
import LocationNotFoundModal from '../components/LocationNotFoundModal';
import MessageSentModal from '../components/MessageSentModal';
import Modal from '../components/Modal';
import PreviousSelector, { Selection } from '../components/PreviousSelector';
import SearchLocationInput from '../components/SearchLocationInput';
import AperioLiveScene from '../components/AperioLiveScene';
import {
  clearAllAddOns,
} from '../features/addOns/addOnsSlice';
import {
  setPostalCode,
} from '../features/postalCode/postalCodeSlice';
import {
  Address,
  setNewServiceAddress,
  setServiceAddress,
  setServiceCoverage,
  clearContact,
} from '../features/serviceLocation/serviceLocationSlice';
import {
  clearAllSelectedServices,
} from '../features/serviceTypes/serviceTypesSlice';
import useAuthentication from '../hooks/useAuthentication';
import usePrefilledAddress from '../hooks/usePrefilledAddress';
import useReferralLink from '../hooks/useReferralLink';
import styles from '../sass/components/Location.module.scss';
import { useTenant } from '../components/TenantContext';
import {
  handoffLanguage,
  marketingWaitlistUrl,
} from '../util/marketingSite';
import { resolvePlacesBounds } from '../util/placesBias';
import { normalizeThemePreset } from '../util/themePreset';
import {
  ROUTE_AUTH_LOGIN,
  ROUTE_AUTH_SIGN_UP,
  ROUTE_BOOK_SERVICES,
} from '../util/constants';
import { getFieldErrors } from '../util/getFieldErrors';
import { graphqlErrorResponse } from '../util/validationErrorHelper';
import {
  GET_ADDRESSES,
  GET_POSTAL,
  GET_POSTAL_WITH_ADDRESS,
  GET_CUSTOMERS_OF_FRANCHISE_MANAGER,
  GET_ALL_CUSTOMERS,
  GET_ADDRESSES_BY_USER_ID,
} from '../util/gql';
import { User } from '../interfaces/User';
import {
  setCustomerId,
  setCustomerName,
} from '../features/customer/customerSlice';
import { selectNewVehicle } from '../features/car/carSlice';
import Badge from '../components/Badge';
import SearchableSelect from '../components/SearchableSelect';

type PostalCheck = {
  getPostal: { isInService: boolean; code?: string | null };
};

export default function Location() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  // Class hook for a tenant theme preset's stylesheet; false for every tenant
  // on the default preset, so nothing about their page changes.
  const themedPage = normalizeThemePreset(tenant?.themePreset)
    !== 'default';
  const [notInServiceModalOpen, setNotInServiceModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [constraints, setConstraints] = useState([]);
  const [
    isValidAddress,
    setIsValidAddress,
  ] = useState<undefined | boolean>(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [customers, setCustomers] = useState<User[]>([]);
  const postalCode = useAppSelector((state) => (
    state.postalCode.postalCode
  ));
  const [location, setLocation] = useState(postalCode);
  // What the visitor needs to do before this step can go on, shown under
  // the address box. Step 1 used to stop them with a grey button and no
  // word of why (QA 2026-09-25 #2).
  const [addressHint, setAddressHint] = useState('');

  const loggedIn = useAppSelector((
    state,
  ) => state.auth.isAuthenticated);

  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const franchiseIdSelect = useAppSelector((state) => state.franchise.id);
  const customer = useAppSelector((state) => state.customer);
  const isSales = currentUser.roles?.includes('sales');

  const {
    selectedAddress,
    serviceAddress,
    newServiceAddress,
  } = useAppSelector((s) => s.serviceLocation);

  const dispatch = useAppDispatch();

  // Address handed off from the marketing site (see hooks/usePrefilledAddress)
  const {
    prefilled: cameFromSite,
    prefillText,
    source: bookingSource,
    lang: handoffLang,
    bias: handoffBias,
    placeId: handoffPlaceId,
    checking: checkingHandoff,
    needsManualPick,
    consumeHandoff,
    siteReported,
  } = usePrefilledAddress();

  // A friend's share link (`?ref=CODE`): kept for the booking and for 30
  // days. Aperio preset only; every other tenant is untouched.
  useReferralLink(
    normalizeThemePreset(tenant?.themePreset) === 'aperio',
    tenant?.subdomain,
  );

  // Aperio attribution / ad events (util/aperioTracking: a no-op for every
  // other tenant). booking_start once per session, on the first step.
  useEffect(() => {
    aprBookingStart();
  }, []);
  // One address_check_submit + one result per address the visitor checks
  // here (util/aperioTracking aprAddressCheck), never a second one for the
  // same address:
  // - an address picked on this page, or "Get Started": submit + result;
  // - the hand-off from the marketing site: the site already counted the
  //   submit, so only the result - and none at all when the site reported
  //   it too (`cov=1`);
  // - an address restored from an earlier visit (on screen before anyone
  //   did anything): nothing, until "Get Started".
  const addressKey = (address?: Address | null) => (address
    ? [address.street, address.postalCode, address.city].join('|').toLowerCase()
    : '');
  const restoredAddressKey = useRef(addressKey(serviceAddress));
  const trackServiceability = (
    inService: boolean,
    address: Address | null | undefined,
    how: 'submit' | 'handoff' | 'auto',
  ) => {
    const key = addressKey(address);
    if (how === 'auto' && (selectedAddress !== 'new' || key === restoredAddressKey.current)) return;
    const fromSite = !!bookingSource && bookingSource.indexOf('web-') === 0;
    aprAddressCheck(
      inService,
      { postal3: postal3(address?.postalCode), city: address?.city || undefined, src: bookingSource || undefined },
      {
        key,
        submit: how !== 'handoff' || !fromSite,
        report: !(how === 'handoff' && siteReported),
      },
    );
  };

  // Rank suggestions towards where the visitor is (hand-off point or city,
  // else their country) - not restrict to it. Same for every tenant.
  const placesBounds = useMemo(
    () => resolvePlacesBounds(handoffBias),
    [handoffBias],
  );
  // The portal's own language when the preset runs it (so the suggestions
  // read in the language on screen); else the site's hand-off, as before.
  const placesLanguage = isPortalI18nActive()
    ? i18n.language
    : handoffLang || handoffLanguage();

  // Aperio only: the address being checked also goes to the coverage log
  // (owner decision 2026-09-25; Aperio's own database, 24 months, never to
  // analytics). A server without those fields yet - a deploy where this
  // client lands first - refuses the query; then this page falls back to
  // the plain query for the rest of the visit and the check goes on.
  const isAperio = normalizeThemePreset(tenant?.themePreset) === 'aperio';
  const [addressLogRefused, setAddressLogRefused] = useState(false);
  const sendAddress = isAperio && !addressLogRefused;
  const postalQuery = sendAddress ? GET_POSTAL_WITH_ADDRESS : GET_POSTAL;
  const [recheckPending, setRecheckPending] = useState(false);

  // The service-area check needs more than the code to be exact: the
  // address's coordinates and country, and its city so the answer can name
  // it. Every field beyond `code` is optional server-side.
  const postalVariables = (address?: Address) => ({
    code: address?.postalCode,
    lat: address?.lat ?? null,
    lng: address?.lng ?? null,
    country: address?.countryCode || null,
    city: address?.city || null,
    // For the demand log only (AperioCoverageLogService).
    region: address?.provinceCode || null,
    deviceId: getDeviceId() || null,
    lang: getBookingLang(),
    ...(sendAddress && checkedAddressFields(
      address,
      selectedAddress === 'new' ? newServiceAddress : undefined,
      handoffPlaceId,
    )),
  });

  // The automatic check's last answer, and the exact question it answered:
  // "Get Started" on that same address acts on it instead of asking the
  // server - and the coverage log - a second time (QA 2026-09-25 #52).
  const lastCheck = useRef<{ key: string; data: PostalCheck } | null>(null);

  useQuery(postalQuery, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      lastCheck.current = {
        key: JSON.stringify(postalVariables(serviceAddress)),
        data,
      };
      const { isInService, code } = data.getPostal;
      if (isInService) {
        dispatch(setPostalCode(code));
      }
      // Kept with the address: a known "not served" closes the next steps
      // (RequireLocation, the step tabs), whatever URL is typed.
      dispatch(setServiceCoverage(!!isInService));
      setIsValidAddress(isInService);
      const handedOff = consumeHandoff();
      trackServiceability(isInService, serviceAddress, handedOff ? 'handoff' : 'auto');
      // Site handoff: the address is already resolved - either the visitor
      // picked it on the marketing site, or we looked their text up here - so
      // don't make them press "Get Started" again. Out of service is the one
      // case that never auto-advances: it gets the same modal as always.
      if (handedOff) {
        if (isInService) {
          navigate(ROUTE_BOOK_SERVICES);
        } else {
          setLocation(code || serviceAddress?.postalCode || '');
          setNotInServiceModalOpen(true);
        }
      } else if (!isInService) {
        // A picked address we cannot serve gets the same modal (waitlist,
        // one-off visit) right away, not a button that silently stays grey.
        setLocation(code || serviceAddress?.postalCode || '');
        setNotInServiceModalOpen(true);
      }
      if (isInService) setAddressHint('');
    },
    // The service-area check failing is not the visitor's problem to solve on
    // a blank screen: end the hand-off and give them the form, with their
    // address already in it, so "Get Started" can try again.
    onError: (error) => {
      if (sendAddress && addressFieldsRefused(error)) {
        // Asked again, without the address fields, on the next render.
        setAddressLogRefused(true);
        return;
      }
      consumeHandoff();
    },
    variables: postalVariables(serviceAddress),
    skip: !serviceAddress?.postalCode,
  });

  // The site could only send free text and the lookup found nothing: put the
  // visitor in the address box, with what they typed, ready to pick.
  useEffect(() => {
    if (!needsManualPick) {
      return;
    }
    const input = document.getElementById('serviceLocation') as HTMLInputElement | null;
    if (!input) {
      return;
    }
    input.focus();
    // Nudge Google's autocomplete into showing its list for the seeded text,
    // so the visitor only has to pick. Harmless when it is not listening.
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }, [needsManualPick]);

  const getErrorMessage = () => {
    const backendError = getFieldErrors(constraints, ['address', 'postalCode']);
    if (serviceAddress && backendError) {
      return t('location.errors.invalidAddress');
    }

    if (serviceAddress && !serviceAddress.postalCode) {
      return t('location.errors.pickFullAddress');
    }

    return addressHint;
  };

  // A new pick (or a new address typed) replaces the old hint.
  useEffect(() => {
    setAddressHint('');
  }, [newServiceAddress]);

  const handleClearSelectedServices = useCallback(() => {
    dispatch(clearAllSelectedServices());
  }, [dispatch]);

  const handleClearSelectedAddOns = useCallback(() => {
    dispatch(clearAllAddOns());
  }, [dispatch]);

  const handleClearSelectedContact = useCallback(() => {
    dispatch(clearContact());
  }, [dispatch]);

  const { logOut, loggedIn: isLoggedIn } = useAuthentication();

  const cleanSelected = () => {
    handleClearSelectedServices();
    handleClearSelectedAddOns();
    handleClearSelectedContact();
  };

  const isFranchiseRole = currentUser.roles?.some(
    (role: string) => [
      'manager',
      'technician',
      'tester',
      'sales'].includes(role),
  );

  const roleLabels: Record<string, string> = {
    manager: 'Manager',
    technician: 'Technician',
    sales: 'Sales',
    tester: 'Tester',
  };

  const roleLabel = [
    'manager',
    'technician',
    'sales',
    'tester'].find((role) => currentUser.roles?.includes(role));

  const {
    data: addressesData,
    loading: addressesLoading,
    error: addressesError,
  } = useQuery(GET_ADDRESSES, {
    skip: !isLoggedIn || isFranchiseRole,
    fetchPolicy: 'network-only',
  });

  const [
    loadCustomerAddressData,
    { data: customerAddressesData },
  ] = useLazyQuery(GET_ADDRESSES_BY_USER_ID);

  useEffect(() => {
    if (customer.id !== undefined) {
      loadCustomerAddressData({
        variables: {
          userId: customer.id,
        },
      });
    }
  }, [customer.id, loadCustomerAddressData]);

  useEffect(() => {
    if (serviceAddress) {
      mp.track('Location - Address Entered', {
        // Every tenant: city, province, country and the first three
        // characters of the postal code only - never the street or the full
        // postal code (util/analyticsScrub enforces the same rule).
        postal3_entered: postal3(serviceAddress.postalCode),
        city_entered: serviceAddress.city,
        province_entered: serviceAddress.provinceCode,
        country_entered: serviceAddress.countryCode,
        is_logged_in: !!loggedIn,
        from_marketing_site: cameFromSite,
        booking_source: bookingSource,
        anon_id: getDistinctId(),
        path: window.location.pathname,
        current_user_role: roleLabel,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newServiceAddress, loggedIn, serviceAddress]);

  useEffect(() => {
    let retrievedAddress;
    if (isFranchiseRole && customerAddressesData) {
      retrievedAddress = customerAddressesData.getAddressesByUserId;
    } else if (addressesData) {
      retrievedAddress = addressesData.getAddresses;
    }

    if (retrievedAddress) {
      setAddresses(retrievedAddress
        .map((a: Address) => (
          pick(
            a,
            [
              'street',
              'city',
              'postalCode',
              'lat',
              'lng',
              'country',
              'province',
              'countryCode',
              'provinceCode',
            ],
          ))));
    }
  }, [addressesData,
    isFranchiseRole,
    customerAddressesData,
    selectedAddress]);

  useEffect(() => {
    if (selectedAddress === 'unset' && addresses.length > 0) {
      dispatch(setServiceAddress([0, addresses[0]]));
    } else if (selectedAddress !== 'new' && selectedAddress !== 'unset') {
      dispatch(
        setServiceAddress([selectedAddress, addresses[selectedAddress]]),
      );
    }
  }, [addresses, selectedAddress, dispatch]);

  const onLocationChecked = (data: PostalCheck) => {
    trackServiceability(!!data?.getPostal.isInService, serviceAddress, 'submit');
    if (data?.getPostal) {
      dispatch(setServiceCoverage(!!data.getPostal.isInService));
    }
    if (data?.getPostal.isInService) {
      if (location !== postalCode) {
        cleanSelected();
      }
      setIsValidAddress(true);
      navigate(ROUTE_BOOK_SERVICES);
    } else {
      setLocation(data?.getPostal?.code || serviceAddress?.postalCode || '');
      setNotInServiceModalOpen(true);
      setIsValidAddress(false);
      cleanSelected();
    }
  };

  const [validateLocation, { loading }] = useLazyQuery(postalQuery, {
    fetchPolicy: 'no-cache',
    onError: (e) => {
      if (sendAddress && addressFieldsRefused(e)) {
        // Ask again without the address fields (the effect below).
        setAddressLogRefused(true);
        setRecheckPending(true);
        return;
      }
      // Not every error carries `extensions.response` (QA #3).
      const response = graphqlErrorResponse(e);
      if (response?.statusCode === 422 && Array.isArray(response.message)) {
        setConstraints(response.message as never[]);
        return;
      }
      setAddressHint(t('location.errors.checkFailed'));
    },
    onCompleted: onLocationChecked,
  });

  // The out-of-service modal offers the marketing site's waitlist when the
  // tenant has one (Aperio); every other tenant gets the modal as it was.
  const outOfServiceAddressText = serviceAddress
    ? [serviceAddress.street, serviceAddress.city, [
      serviceAddress.provinceCode, serviceAddress.postalCode,
    ].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : location;
  const waitlistUrl = themedPage
    ? marketingWaitlistUrl(
      tenant?.marketingSiteUrl,
      outOfServiceAddressText,
      placesLanguage,
    )
    : null;

  const addressStrings = addresses
    .map((a) => `${a.street}, ${a.city} ${a.postalCode}`);

  const checkLocation = () => {
    setConstraints([]);
    // Say what is missing instead of doing nothing (QA #2).
    if (!serviceAddress) {
      setAddressHint(t('location.errors.pickFromList'));
      document.getElementById('serviceLocation')?.focus();
      return;
    }
    if (!serviceAddress.postalCode) {
      document.getElementById('serviceLocation')?.focus();
      return;
    }
    setAddressHint('');
    // The picked address's own postal code. The stored one is only set for
    // an address in service, so an uncovered address used to check nothing.
    const code = serviceAddress.postalCode || postalCode;
    if (code) {
      const variables = { ...postalVariables(serviceAddress), code };
      const answered = lastCheck.current;
      if (answered && answered.key === JSON.stringify(variables)) {
        onLocationChecked(answered.data);
        return;
      }
      validateLocation({ variables });
    }
  };

  // The server refused the address fields: the same check, asked again
  // with the plain query this render now holds.
  useEffect(() => {
    if (recheckPending && !sendAddress) {
      setRecheckPending(false);
      checkLocation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckPending, sendAddress]);

  const onChangeAddressSelector = useCallback((s: Selection) => {
    if (s === 'new' || s === 'unset') {
      setIsValidAddress(false);
      dispatch(setNewServiceAddress(newServiceAddress));
    } else {
      dispatch(setServiceAddress([s, addresses[s]]));
    }
  }, [dispatch, newServiceAddress, addresses]);

  const goToLogin = () => {
    navigate(ROUTE_AUTH_LOGIN);
  };

  const goToSignUp = () => {
    navigate(ROUTE_AUTH_SIGN_UP);
  };

  const tryAnotherCode = () => {
    setNotInServiceModalOpen(!notInServiceModalOpen);
    setLocation('');
    // Closing the modal leaves the address in the box but never makes it
    // bookable: it stays marked "not served" until another one is picked.
    if (notInServiceModalOpen && serviceAddress) {
      dispatch(setServiceCoverage(false));
    }
    if (notInServiceModalOpen && serviceAddress?.postalCode) {
      setAddressHint(t('location.errors.notServedYet'));
    }
  };

  const displaySuccess = () => {
    setNotInServiceModalOpen(false);
    setSuccessModalOpen(true);
  };

  const queryToUse = isSales
    ? GET_ALL_CUSTOMERS
    : GET_CUSTOMERS_OF_FRANCHISE_MANAGER;

  const {
    data: customersData,
  } = useQuery(queryToUse, {
    variables: {
      franchiseId: franchiseIdSelect || currentUser.franchiseId,
    },
    skip: !isFranchiseRole,
    fetchPolicy: 'network-only',
  });

  const selectCustomer = useCallback((id, name) => {
    if (id !== customer.id) {
      dispatch(selectNewVehicle());
      dispatch(setCustomerId(id));
    }
    if (name !== customer.name) {
      dispatch(setCustomerName(name));
    }
  }, [customer.id, customer.name, dispatch]);

  const handleCustomerChange = (selectedCustomerId: number) => {
    const customerName = customers.find(
      (user: User) => user.id === selectedCustomerId,
    );
    selectCustomer(selectedCustomerId, customerName?.name);
  };

  useEffect(() => {
    if (customersData) {
      const result = isSales
        ? customersData.getAllCustomers
        : customersData.getCustomersOfFranchiseManager;
      setCustomers(result
        .map((u: User) => (
          pick(
            u,
            [
              'name',
              'id',
            ],
          ))));
    }
  }, [customersData, isSales]);
  return (
    // `apr-book-page` is a class hook for a tenant theme preset's stylesheet:
    // it lays the page out as the marketing site's hero (form left, scene
    // right) and swaps the decorative art. Absent for every tenant on the
    // default preset, so their class list and layout are unchanged.
    <div className={cx(styles.wrapper, { 'apr-book-page': themedPage })}>
      <div>
        <Container>
          <Row>
            <Column
              sm={12}
              md={6}
              mdOffset={3}
            >
              {/*
                Hand-off in progress. The address form stays MOUNTED but
                hidden underneath this panel: it is what loads the Google
                Places script this lookup waits on, and it keeps the seeded
                text ready in case we end up handing the page back. Rendering
                it visibly first is what used to show a flash of an address
                form the visitor is not meant to fill in.
              */}
              {checkingHandoff && (
                <div className={cx(styles.card, styles.checkingCard)}>
                  <div role="status" aria-live="polite">
                    <h1>{t('location.checking.title')}</h1>
                    <p>{t('location.checking.body')}</p>
                  </div>
                  <Loader />
                </div>
              )}
              <div
                className={styles.card}
                style={checkingHandoff ? { display: 'none' } : undefined}
                aria-hidden={checkingHandoff || undefined}
              >
                <Container fluid>
                  <Row>
                    <Column
                      xs={12}
                    >
                      {isFranchiseRole && roleLabel && (
                        <Badge
                          variant="primary"
                        >
                          Franchise
                          {' '}
                          {roleLabels[roleLabel]}
                        </Badge>
                      )}
                      <div className={styles.header}>
                        <h1>
                          {isFranchiseRole
                            ? 'Select Customer and Address'
                            : t('location.title')}
                        </h1>
                        <p>
                          {isFranchiseRole
                            // eslint-disable-next-line max-len
                            ? 'If you are creating a booking for a new customer, leave the customer field blank and only enter the service address.'
                            : ''}
                        </p>
                        <br />
                        <p>
                          {isFranchiseRole
                            // eslint-disable-next-line max-len
                            ? 'Select the customer you are booking for and their service address.'
                            : t('location.subtitle')}
                        </p>
                      </div>
                    </Column>
                  </Row>
                  {isFranchiseRole && (
                    <Row>
                      <SearchableSelect
                        title="Customer"
                        value={customer.id
                          ? { label: customer.name, value: customer.id }
                          : undefined}
                        placeholder="Select a customer"
                        addMargin
                        customers={customers.map((m: {
                        name: string;
                        id: number;
                      }) => ({
                          value: m.id,
                          label: m.name,
                        }))}
                        onSelect={(e) => handleCustomerChange(e)}
                      />
                    </Row>
                  )}
                  <Row>
                    {customer.id
                       && (
                       <div className={styles.label}>
                         <br />
                         <p className="label"> Service Address </p>
                       </div>
                       )}
                    <Column xs={12}>
                      <PreviousSelector
                        data={addressStrings}
                        newLabel={addressStrings.length
                          ? t('location.addAnother')
                          : t('location.yourServiceAddress')}
                        loading={addressesLoading}
                        error={!!addressesError}
                        name="address"
                        selected={selectedAddress}
                        onChange={onChangeAddressSelector}
                        className={styles.addressSelector}
                      >
                        <SearchLocationInput
                          className={styles.locationInput}
                          id="serviceLocation"
                          name="street-address"
                          autoComplete="street-address"
                          value={newServiceAddress}
                          initialValue={prefillText}
                          bounds={placesBounds}
                          language={placesLanguage}
                          onChange={(a) => dispatch(setNewServiceAddress(a))}
                          title={isFranchiseRole
                            && customer.id ? '' : t('location.serviceAddress')}
                          errorMessage={getErrorMessage()}
                          success={!!serviceAddress && isValidAddress}
                        />
                      </PreviousSelector>
                      <Button
                        variant="primary"
                        size="large"
                        loading={loading}
                        onClick={checkLocation}
                        className={styles.goButton}
                        // Grey until the address checks out, but focusable
                        // and clickable: the click says what is missing.
                        softDisabled={isValidAddress === false}
                      >
                        {t('location.getStarted')}
                      </Button>
                      <div className={styles.loginSection}>
                        {!loggedIn ? (
                          <>
                            <span className={cx(styles.loginHeader, 'label')}>
                              {t('location.haveAccount')}
                            </span>
                            <Button
                              variant="secondary"
                              className={styles.loginButton}
                              onClick={goToLogin}
                              size="large"
                            >
                              {t('location.logIn')}
                            </Button>

                            <div className={styles.loginRow}>
                              <span>
                                {t('location.noAccount')}
                                {' '}
                                <Button
                                  variant="link"
                                  className={styles.signUpLink}
                                  onClick={goToSignUp}
                                >
                                  {t('location.signUp')}
                                </Button>
                              </span>
                            </div>
                          </>
                        ) : (
                          <Button
                            variant="secondary"
                            className={styles.loginButton}
                            onClick={logOut}
                            size="large"
                          >
                            {t('location.logOut')}
                          </Button>
                        )}
                      </div>
                    </Column>
                  </Row>
                </Container>
              </div>
              <Modal
                open={notInServiceModalOpen}
                disableBackdropClick
                onClose={tryAnotherCode}
              >
                <Form>
                  <LocationNotFoundModal
                    onSubmit={displaySuccess}
                    onTryAnotherPostalCode={tryAnotherCode}
                    postalCode={location}
                    waitlistUrl={waitlistUrl}
                    addressText={outOfServiceAddressText}
                    knownEmail={currentUser?.email}
                  />
                </Form>
              </Modal>
              <Modal
                open={successModalOpen}
                disableBackdropClick
                onClose={() => setSuccessModalOpen(false)}
              >
                <MessageSentModal
                  onClose={() => setSuccessModalOpen(false)}
                />
              </Modal>
            </Column>
          </Row>
        </Container>
      </div>
      {/*
        The marketing hero's live scene. It renders nothing unless the tenant
        is on the Aperio preset, so every other tenant's page is unchanged -
        no markup, no stylesheet, no script. Last in the DOM on purpose: on a
        phone that puts it below the form, which stays the LCP element, and on
        a desktop live-scene.css lifts it into the right-hand column the
        tenant's still image used to fill.
      */}
      {/* Remounted on a language switch: the scene reads its words once. */}
      <AperioLiveScene key={i18n.language} />
    </div>
  );
}
